import { Pipe, PipeTransform } from '@angular/core'
import { findLinks } from './utils/link-detection.util'
import { maskStateCode, STATE_CODE_REGEX } from './utils/corp-member.util'

// Matches inline @mention tokens, e.g. `@[[n3k2a1f94c:Chidi O]]` — the first capture is a
// CorpMember slug_id (legacy numeric ids are also matched, for messages sent before the
// slug_id migration). Must stay in sync with MENTION_TOKEN_REGEX in
// corpers.online/utilities/mentions.js.
const MENTION_TOKEN_REGEX = /@\[\[([A-Za-z0-9_-]{1,32})(?::([^\]]*))?\]\]/g

export interface MessageSegment {
  type: 'text' | 'mention' | 'link'
  value: string
  id?: string
  // Only set for 'link' segments — the fully-qualified URL to navigate to (adds `https://` to
  // bare `www.` links), since `value` keeps the original text as the user typed it.
  url?: string
}

@Pipe({
  name: 'parseMentions',
})
export class ParseMentionsPipe implements PipeTransform {
  transform(message: string | null | undefined): MessageSegment[] {
    if (!message) return []

    const segments: MessageSegment[] = []
    let lastIndex = 0

    for (const match of message.matchAll(MENTION_TOKEN_REGEX)) {
      const [token, id, name] = match
      const matchIndex = match.index ?? 0

      if (matchIndex > lastIndex) {
        segments.push(...this.parseLinks(message.slice(lastIndex, matchIndex)))
      }
      // Mentions of a member without a name carry their state code - masked, as everywhere in
      // chat (messages sent before that was masked when the mention was made).
      const value = STATE_CODE_REGEX.test(name ?? '') ? maskStateCode(name) : name || 'Member'
      segments.push({ type: 'mention', value, id })
      lastIndex = matchIndex + token.length
    }

    if (lastIndex < message.length) {
      segments.push(...this.parseLinks(message.slice(lastIndex)))
    }

    return segments
  }

  /** Splits a plain-text (non-mention) chunk into text/link segments. */
  private parseLinks(text: string): MessageSegment[] {
    if (!text) return []

    const segments: MessageSegment[] = []
    let lastIndex = 0

    for (const link of findLinks(text)) {
      if (link.index > lastIndex) {
        segments.push({ type: 'text', value: text.slice(lastIndex, link.index) })
      }
      segments.push({ type: 'link', value: link.text, url: link.url })
      lastIndex = link.end
    }

    if (lastIndex < text.length) {
      segments.push({ type: 'text', value: text.slice(lastIndex) })
    }

    return segments
  }
}
